import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AxiosRequestConfig } from 'axios';
import type { User, UserRole } from '@/types';
import { api, waitForSessionRefresh } from '@/lib/api';
import { beginClientLogout, clearClientLogout, isClientLogoutInProgress, purgeClientSessionOnLogout } from '@/lib/auth-bootstrap';
import { connectSocket, disconnectSocket } from '@/lib/socket';
import type { LoginResponse, RegisterRequest, RegisterResponse, UpdateProfileRequest } from '@/types/api';

/** Décode le payload JWT (sans vérifier la signature) — utilisé pour les liens d'accès apprenant EduFlow. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Identifiant utilisateur porté par le jeton (accès classique ou apprenant). */
function jwtSubjectUserId(payload: Record<string, unknown> | null): string | null {
  if (!payload) return null;
  if (typeof payload.userId === 'string') return payload.userId;
  if (typeof payload.sub === 'string') return payload.sub;
  return null;
}

/**
 * Réhydrate `user` depuis le persist seulement s’il correspond au sujet du JWT courant.
 * Évite d’afficher un ancien compte alors que `auth_token` pointe vers un autre utilisateur.
 */
function persistedUserMatchingToken(token: string | null, persistedUser: User | null | undefined): User | null {
  if (!token?.trim() || !persistedUser) return null;
  const payload = decodeJwtPayload(token);
  const uid = jwtSubjectUserId(payload);
  if (!uid || persistedUser.id !== uid) return null;
  return persistedUser;
}

/** Destination renvoyée par `POST /auth/web-handoff/exchange` (chemin interne du site). */
export interface WebHandoffDestination {
  kind: 'flow' | 'path' | 'webinar';
  path: string;
  flowId?: string;
  blockId?: string;
  assignmentId?: string;
  joinCode?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<void>;
  loginWithOTP: (email: string, otp: string) => Promise<void>;
  /** Session ouverte depuis l'app mobile (code à usage unique de `/handoff`) ; renvoie la page à ouvrir. */
  loginWithWebHandoff: (code: string) => Promise<WebHandoffDestination>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => void;
  updateProfile: (data: UpdateProfileRequest) => Promise<void>;
  refreshAuthToken: () => Promise<void>;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  fetchProfile: () => Promise<void>;
}

function handleLoginSuccess(
  set: (partial: Partial<AuthState>) => void,
  response: any,
) {
  clearClientLogout();
  // Backend sends { success, data: { user, accessToken } } with refreshToken in cookie
  const payload = response.data || response;
  const token = payload.accessToken || payload.token;
  const rawUser = payload.user;
  const user = rawUser
    ? { ...rawUser, avatar: rawUser.avatar ?? rawUser.avatarUrl ?? undefined }
    : rawUser;

  // Session « vraie » (mot de passe, Google, etc.) : ne pas réutiliser un profil apprenant résiduel
  localStorage.removeItem('auth_user');

  if (token) {
    localStorage.setItem('auth_token', token);
  }
  const refreshToken = payload.refreshToken ?? null;
  if (refreshToken) {
    localStorage.setItem('auth_refresh_token', refreshToken);
  } else {
    localStorage.removeItem('auth_refresh_token');
  }
  set({
    user,
    token,
    refreshToken,
    isAuthenticated: true,
    isLoading: false,
  });
  if (token) {
    connectSocket(token);
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: localStorage.getItem('auth_token'),
      refreshToken: localStorage.getItem('auth_refresh_token'),
      isAuthenticated: !!localStorage.getItem('auth_token'),
      isLoading: false,

      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const response = await api.post<LoginResponse>('/auth/login', { email, password });
          handleLoginSuccess(set, response);
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      loginWithWebHandoff: async (code) => {
        set({ isLoading: true });
        try {
          const response = await api.post<any>(
            '/auth/web-handoff/exchange',
            { code },
            { withCredentials: true, skipDefaultAuth: true } as AxiosRequestConfig & { skipDefaultAuth?: boolean },
          );
          const payload = (response as any)?.data ?? response;
          // Autre compte déjà connecté dans ce navigateur : on efface ses traces locales (progression, brouillons).
          const previousUid = jwtSubjectUserId(decodeJwtPayload(localStorage.getItem('auth_token') ?? ''));
          const nextUid = jwtSubjectUserId(decodeJwtPayload(String(payload?.accessToken ?? '')));
          if (previousUid && nextUid && previousUid !== nextUid) purgeClientSessionOnLogout();
          handleLoginSuccess(set, response);
          const destination = payload?.destination as WebHandoffDestination | undefined;
          return destination && typeof destination.path === 'string'
            ? destination
            : { kind: 'flow', path: '/learner' };
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      loginWithOTP: async (email, otp) => {
        set({ isLoading: true });
        try {
          const response = await api.post<LoginResponse>('/auth/otp/verify', { email, otp });
          handleLoginSuccess(set, response);
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (data) => {
        set({ isLoading: true });
        try {
          const response = await api.post<any>('/auth/register', data);
          handleLoginSuccess(set, response);
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        const already = isClientLogoutInProgress();
        if (!already) beginClientLogout();
        const accessToken =
          get().token || (typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null);
        const refreshToken =
          get().refreshToken ||
          (typeof localStorage !== 'undefined' ? localStorage.getItem('auth_refresh_token') : null);
        if (!already) disconnectSocket();
        const finishLocal = () => {
          purgeClientSessionOnLogout();
          try {
            useAuthStore.persist.clearStorage();
          } catch {
            /* noop */
          }
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        };
        finishLocal();
        if (already) return;
        const headers: Record<string, string> = {};
        if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
        void (async () => {
          try {
            await waitForSessionRefresh();
          } catch {
            /* un refresh déjà parti ne doit pas recréer la session */
          }
          try {
            await api.post(
              '/auth/logout',
              { refreshToken: refreshToken ?? undefined },
              { withCredentials: true, headers, skipDefaultAuth: true },
            );
          } catch {
            /* le cookie est quand même effacé si le serveur répond */
          }
          finishLocal();
        })();
      },

      updateProfile: async (data) => {
        const response = await api.put<any>('/auth/me', data);
        const payload = (response as any)?.data ?? response;
        set({ user: payload as User });
      },

      refreshAuthToken: async () => {
        if (isClientLogoutInProgress()) {
          throw new Error('logout');
        }
        const currentRefreshToken = get().refreshToken;
        const body = currentRefreshToken ? { refreshToken: currentRefreshToken } : {};
        const refreshConfig = {
          withCredentials: true,
          skipDefaultAuth: true,
        } as AxiosRequestConfig & { skipDefaultAuth?: boolean };
        try {
          const raw = await api.post<{
            success?: boolean;
            data?: { accessToken?: string; token?: string; refreshToken?: string };
          }>('/auth/refresh', body, refreshConfig);
          const payload = raw && typeof raw === 'object' && 'data' in raw && raw.data ? raw.data : raw;
          const access =
            payload && typeof payload === 'object'
              ? (payload as { accessToken?: string; token?: string }).accessToken ??
                (payload as { token?: string }).token
              : undefined;
          const newRt =
            payload && typeof payload === 'object'
              ? (payload as { refreshToken?: string }).refreshToken
              : undefined;
          if (!access) {
            throw new Error('Réponse refresh sans access token');
          }
          if (isClientLogoutInProgress()) {
            throw new Error('logout');
          }
          localStorage.setItem('auth_token', access);
          if (newRt) {
            localStorage.setItem('auth_refresh_token', newRt);
          }
          set({
            token: access,
            refreshToken: newRt ?? get().refreshToken,
            isAuthenticated: true,
          });
          connectSocket(access);
        } catch (error) {
          // Hors ligne : garder la session (formations téléchargées, progression en file) ; nouvel essai au retour du réseau.
          const offline =
            (typeof navigator !== 'undefined' && navigator.onLine === false) ||
            (error as { statusCode?: number } | null)?.statusCode === 0;
          if (offline) throw new Error('refresh_offline');
          get().logout();
          throw new Error('refresh_failed');
        }
      },

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setToken: (token) => {
        if (token) {
          localStorage.setItem('auth_token', token);
          const payload = decodeJwtPayload(token);
          const isLearnerAccess =
            payload?.typ === 'eduflow_learner_access' &&
            typeof payload.userId === 'string' &&
            typeof payload.email === 'string';
          if (isLearnerAccess) {
            const firstName = typeof payload.firstName === 'string' ? payload.firstName : '';
            const lastName = typeof payload.lastName === 'string' ? payload.lastName : '';
            localStorage.setItem(
              'auth_user',
              JSON.stringify({
                id: payload.userId,
                email: payload.email,
                firstName,
                lastName,
                role: String(payload.role ?? 'student'),
              }),
            );
            set({
              token,
              isAuthenticated: true,
              user: {
                id: String(payload.userId),
                email: String(payload.email),
                firstName,
                lastName,
                role: String(payload.role ?? 'student') as User['role'],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                isActive: true,
                xp: 0,
                level: 1,
                streak: 0,
                badges: [],
                preferences: {
                  theme: 'system',
                  locale: 'fr',
                  fontSize: 'medium',
                  highContrast: false,
                  reducedMotion: false,
                  dyslexiaMode: false,
                  emailNotifications: true,
                  pushNotifications: false,
                  soundEnabled: true,
                  autoSave: true,
                  compactView: false,
                },
              },
            });
            return;
          }
          // Jeton classique (ex. après login) : ne pas garder un `user` obsolète en mémoire
          localStorage.removeItem('auth_user');
          const currentUser = get().user;
          const newUid = jwtSubjectUserId(payload);
          if (newUid && currentUser?.id === newUid) {
            set({ token, isAuthenticated: true });
          } else {
            set({ token, isAuthenticated: true, user: null });
          }
          queueMicrotask(() => {
            void get().fetchProfile();
          });
          return;
        } else {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
        }
        set({ token, isAuthenticated: !!token });
      },

      fetchProfile: async () => {
        set({ isLoading: true });
        try {
          const response = await api.get<any>('/auth/me');
          const raw = (response as any)?.data ?? response;
          const user = {
            ...raw,
            avatar: raw.avatar ?? raw.avatarUrl ?? undefined,
          };
          set({ user: user as User, isLoading: false });
        } catch {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      version: 3,
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
      }),
      migrate: (persistedState, fromVersion) => {
        if (!persistedState || typeof persistedState !== 'object') return persistedState as never;
        // v2 : pas de `user` persisté — laisser tel quel ; le merge + fetchProfile feront le reste
        if (fromVersion < 2) {
          const s = { ...(persistedState as Record<string, unknown>) };
          delete s.user;
          return s as never;
        }
        return persistedState as never;
      },
      /**
       * `localStorage.auth_token` prime sur le jeton persisté.
       * On ne réhydrate `user` depuis le disque que si son id = userId du JWT (même compte que le jeton).
       */
      merge: (persisted, current) => {
        const p = persisted as Partial<
          Pick<AuthState, 'token' | 'refreshToken' | 'isAuthenticated' | 'user'>
        > | null;
        const lsToken =
          typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
        const storedToken = typeof p?.token === 'string' ? p.token : null;
        const token = (lsToken?.trim() ? lsToken : null) || storedToken || current.token;
        if (token && typeof window !== 'undefined') {
          localStorage.setItem('auth_token', token);
        }
        const lsRt =
          typeof window !== 'undefined' ? localStorage.getItem('auth_refresh_token') : null;
        const refreshToken =
          (lsRt?.trim() ? lsRt : null) ||
          (typeof p?.refreshToken === 'string' ? p.refreshToken : null) ||
          current.refreshToken;
        const isAuthenticated = Boolean(token);
        const safeUser = persistedUserMatchingToken(token, p?.user as User | null | undefined);
        return {
          ...current,
          token,
          refreshToken,
          isAuthenticated,
          user: safeUser,
        };
      },
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        if (state.token && typeof window !== 'undefined') {
          localStorage.setItem('auth_token', state.token);
        }
        if (state.token && state.isAuthenticated) {
          queueMicrotask(() => {
            void useAuthStore.getState().fetchProfile();
          });
        }
      },
    },
  ),
);
